// Sprint-017 E7 — the DE-IDENTIFIED assignee adapter (ADR-003 §4.5, design
// §7, CD sign-off Condition 1 / Ruling Q-A). **P0 PRIVACY BOUNDARY.**
//
// A project task individually assigned to a person surfaces on their normal
// Today / My list looking EXACTLY like an ordinary handed-down chore — no
// project name, id, badge, marker, chevron, tooltip, or tap-path back to the
// project, anywhere. This adapter maps the five-field `MyProjectTaskOut` (the
// only shape the server ever exposes for this feed) onto the EXISTING
// `DueTask` shape so the EXISTING, unmodified `TaskRow` renders it. There is
// deliberately no `ProjectTaskRow` component.
//
// The one real DOM leak vector is `data-task-kind` (TaskRow emits
// `data-task-kind={task.kind}`). Ruling Q-A: the adapter MUST emit the SAME
// neutral `kind` token ordinary assigned work already uses — `"assignment"`
// — NOT a `project_task` value and NOT a new opaque one. Reusing the existing
// assigned-work kind is what makes a DOM / screenshot diff of a de-identified
// project task vs. an ordinary assigned chore *truly* identical. This is the
// boundary; the durable test `project-task-deidentified.test.tsx` asserts it.

import type { DueTask, MyProjectTaskOut } from "../types";

/** The neutral, existing assigned-work `kind` token. An undated ad-hoc /
 * handed-down assignment carries this today (`TaskKind = "cadence" |
 * "assignment"`). The de-identified row reuses it verbatim — no
 * project-revealing kind ever reaches the DOM. */
export const NEUTRAL_ASSIGNED_KIND = "assignment" as const;

/** The server-rendered meta phrase for an undated, standalone assigned task
 * (`app/engine/copy.py::ad_hoc_phrase(None)` → `"one-time"`). A de-identified
 * project task carries no due date and no parent, so it renders the SAME meta
 * phrase an ordinary undated handed-down chore does — reinforcing that the
 * two are indistinguishable. */
export const UNDATED_ASSIGNED_PHRASE = "one-time";

/**
 * Map a de-identified `/me/project-tasks` feed item to a `DueTask`.
 *
 * Deliberate field choices (each preserves indistinguishability):
 * - `kind: "assignment"` — the neutral existing kind (Ruling Q-A). NEVER a
 *   project-revealing value.
 * - `assignee_id: meId` (non-null) — an *assigned* row carries NO "Up for
 *   grabs" badge (that badge is only for `assignee_id === null` unowned
 *   chores). So the row renders title + meta phrase and nothing else.
 * - `due_date: null`, `parent_assignment_id: null` — lands in "Whenever you
 *   get a chance", merged inline with ordinary undated work; never a separate
 *   "assigned to me" section.
 * - `area`/`assignee_name`/`overdue_*`: null — no extra text, no project tell.
 *
 * The `safety` flag is intentionally NOT surfaced on this row: `TaskRow` (and
 * therefore every ordinary chore row) renders no safety affordance, so
 * surfacing it here would make the project row *distinguishable* — the exact
 * opposite of the boundary. Safety styling lives only inside the project
 * (where the row is not de-identified).
 *
 * @param meId the signed-in user's own id — the row's non-null assignee.
 */
export function adaptMyProjectTask(item: MyProjectTaskOut, meId: string): DueTask {
  return {
    kind: NEUTRAL_ASSIGNED_KIND,
    id: item.id,
    title: item.title,
    area: null,
    assignee_id: meId,
    assignee_name: null,
    parent_assignment_id: null,
    due_date: null,
    phrase: UNDATED_ASSIGNED_PHRASE,
    overdue_since: null,
    overdue_phrase: null,
  };
}
